import {
  SummaryCell,
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import { useAppSelector } from 'store/hooks';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import { EffortContactItem } from 'library/types';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { CreateEffortContactDialog, DeleteEffortContactDialog, UpdateEffortContactDialog } from './components';

const EffortContacts = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [effortContacts, setEffortContacts] = useState<EffortContactItem[]>([]);
  const [openDeleteEffortContact, setOpenDeleteEffortContact] = useState(false);
  const [openCreateEffortContact, setOpenCreateEffortContact] = useState(false);
  const [openUpdateEffortContact, setOpenUpdateEffortContact] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'gibTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
      {
        field: 'emails',
        headerName: intl.formatMessage({ id: 'effortContacts.emails', defaultMessage: 'E-Postalar' }),
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: SummaryCell({ mode: 'emails' }),
        valueGetter: (_value, row) => (row.emails ?? []).join('; '),
      },
    ],
    [intl]
  );

  // sync helper functions
  const handleCloseDeleteEffortContact = () => setOpenDeleteEffortContact(false);
  const handleClickOpenDeleteEffortContact = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // non-deletable item control
    if (effortContacts.find((effortContact) => effortContact.id === rowSelectionModel[0])?.emails === null) {
      enqueueSnackbar(<FormattedMessage id="checkbox.canNotDelete" defaultMessage="Bu satır silinemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteEffortContact(true);
  };

  const handleCloseCreateEffortContact = () => setOpenCreateEffortContact(false);
  const handleClickOpenCreateEffortContact = () => setOpenCreateEffortContact(true);

  const handleCloseUpdateEffortContact = () => setOpenUpdateEffortContact(false);
  const handleClickOpenUpdateEffortContact = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // non-editable item control
    if (effortContacts.find((effortContact) => effortContact.id === rowSelectionModel[0])?.emails === null) {
      enqueueSnackbar(<FormattedMessage id="checkbox.canNotUpdate" defaultMessage="Bu satır düzenlenemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateEffortContact(true);
  };

  // async helper functions
  const getEffortContacts = async () => {
    try {
      setLoading(true);

      const response = await apiService.getEffortContacts();

      if (response.isSuccess) {
        setEffortContacts(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-effort-contact')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getEffortContacts();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-effort-contact']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateEffortContact}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="effortContacts.createEffortContact" defaultMessage="Efor Muhatabı Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-effort-contact']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateEffortContact}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="effortContacts.updateEffortContact" defaultMessage="Efor Muhatabı Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-effort-contact']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteEffortContact}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="effortContacts.deleteEffortContact" defaultMessage="Efor Muhatabı Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateEffortContactDialog
        open={openCreateEffortContact}
        handleClose={handleCloseCreateEffortContact}
        refresh={getEffortContacts}
      />
      <UpdateEffortContactDialog
        open={openUpdateEffortContact}
        effortContactId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateEffortContact}
        refresh={getEffortContacts}
      />
      <DeleteEffortContactDialog
        open={openDeleteEffortContact}
        effortContactId={rowSelectionModel[0]}
        handleClose={handleCloseDeleteEffortContact}
        refresh={getEffortContacts}
        setRowSelectionModel={setRowSelectionModel}
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={effortContacts}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default EffortContacts;
